/**
 * TemplateEditor Component
 * Form for creating and editing notification templates
 */

import { useState, useMemo } from 'react';
import { X, Save, AlertCircle, Plus, Trash2, Variable } from 'lucide-react';
import clsx from 'clsx';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Button } from '@/components/ui/Button';
import { Card, CardHeader, CardContent, CardFooter } from '@/components/ui/Card';
import { ChannelPicker } from './ChannelPicker';
import { ActionPicker } from './ActionPicker';
import { NotificationPreview } from './NotificationPreview';
import { useCreateTemplate, useUpdateTemplate } from '../hooks/useNotificationTemplates';
import type { NotificationTemplate, NotificationTemplateCreate, Channel, NotificationContent, TemplateVariable, VariableType } from '../types';

interface TemplateEditorProps {
  template?: NotificationTemplate;
  onClose: () => void;
  onSuccess?: () => void;
  className?: string;
}

/**
 * Extract variable names from template content
 * Matches patterns like {variable_name}
 */
function extractVariablesFromContent(content: NotificationContent): string[] {
  const text = `${content.title || ''} ${content.body || ''} ${content.action_label || ''}`;
  const matches = text.match(/\{(\w+)\}/g) || [];
  const variables = matches.map(m => m.slice(1, -1)); // Remove { and }
  return [...new Set(variables)]; // Remove duplicates
}

export function TemplateEditor({ template, onClose, onSuccess, className }: TemplateEditorProps) {
  const isEditing = !!template;

  // Form state
  const [name, setName] = useState(template?.name || '');
  const [displayName, setDisplayName] = useState(template?.display_name || '');
  const [description, setDescription] = useState(template?.description || '');
  const [channels, setChannels] = useState<Channel[]>(template?.channels || []);
  const [content, setContent] = useState<NotificationContent>(
    template?.content || { title: '', body: '' }
  );
  const [variables, setVariables] = useState<TemplateVariable[]>(template?.variables || []);
  const [isActive, setIsActive] = useState(template?.is_active ?? true);

  // Validation errors
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Extract variables from content to show suggestions
  const contentVariables = useMemo(() => extractVariablesFromContent(content), [content]);
  const undefinedVariables = useMemo(
    () => contentVariables.filter(v => !variables.some(def => def.name === v)),
    [contentVariables, variables]
  );

  // Variable management functions
  const addVariable = (varName?: string) => {
    const newVar: TemplateVariable = {
      name: varName || '',
      label: varName || '',
      type: 'string',
      required: true,
    };
    setVariables(prev => [...prev, newVar]);
  };

  const updateVariable = (index: number, field: keyof TemplateVariable, value: string | boolean) => {
    setVariables(prev => prev.map((v, i) => i === index ? { ...v, [field]: value } : v));
  };

  const removeVariable = (index: number) => {
    setVariables(prev => prev.filter((_, i) => i !== index));
  };

  const createMutation = useCreateTemplate();
  const updateMutation = useUpdateTemplate();

  const isPending = createMutation.isPending || updateMutation.isPending;

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = '模板名称不能为空';
    } else if (!/^[a-z_][a-z0-9_]*$/.test(name)) {
      newErrors.name = '模板名称只能包含小写字母、数字和下划线，且不能以数字开头';
    }

    if (!displayName.trim()) {
      newErrors.displayName = '显示名称不能为空';
    }

    if (channels.length === 0) {
      newErrors.channels = '请至少选择一个通知渠道';
    }

    if (!content.body?.trim()) {
      newErrors.body = '通知正文不能为空';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;

    try {
      if (isEditing) {
        await updateMutation.mutateAsync({
          name: template.name,
          data: {
            display_name: displayName,
            description: description || undefined,
            channels,
            content,
            variables,
            is_active: isActive,
          },
        });
      } else {
        const data: NotificationTemplateCreate = {
          name,
          display_name: displayName,
          description: description || undefined,
          channels,
          content,
          variables,
          is_active: isActive,
        };
        await createMutation.mutateAsync(data);
      }

      onSuccess?.();
      onClose();
    } catch (error) {
      console.error('Failed to save template:', error);
      setErrors({
        submit: `保存失败: ${error instanceof Error ? error.message : '未知错误'}`,
      });
    }
  };

  const handleContentChange = (field: keyof NotificationContent, value: string) => {
    setContent(prev => ({ ...prev, [field]: value }));
  };

  return (
    <Card className={clsx('w-full', className)} padding={false}>
      <CardHeader className="px-6 pt-6 pb-0">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">
            {isEditing ? '编辑通知模板' : '新建通知模板'}
          </h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </CardHeader>

      <CardContent className="p-6">
        {/* Two-column layout on wide screens */}
        <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
          {/* Left Column: Form Fields */}
          <div className="space-y-6">
            {/* Basic Info */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                基本信息
              </h3>

              <Input
                label="模板名称 (唯一标识)"
                value={name}
                onChange={e => setName(e.target.value)}
                placeholder="例如: lucky_charm_ready"
                disabled={isEditing || isPending}
                error={errors.name}
                helperText="小写字母、数字和下划线，用于 API 调用"
              />

              <Input
                label="显示名称"
                value={displayName}
                onChange={e => setDisplayName(e.target.value)}
                placeholder="例如: 幸运符生成完成"
                disabled={isPending}
                error={errors.displayName}
              />

              <Textarea
                label="描述 (可选)"
                value={description}
                onChange={e => setDescription(e.target.value)}
                placeholder="描述此模板的用途"
                rows={2}
                disabled={isPending}
              />

              <label className="flex items-center gap-2 cursor-pointer">
                <input
                  type="checkbox"
                  checked={isActive}
                  onChange={e => setIsActive(e.target.checked)}
                  disabled={isPending}
                  className="rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                />
                <span className="text-sm text-gray-700">启用此模板</span>
              </label>
            </div>

            {/* Channels */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                通知渠道
                {errors.channels && (
                  <span className="ml-2 text-red-500 font-normal">({errors.channels})</span>
                )}
              </h3>

              <ChannelPicker
                value={channels}
                onChange={setChannels}
                disabled={isPending}
              />
            </div>

            {/* Content */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
                通知内容
              </h3>

              {/* Title - only for push */}
              {channels.includes('push') && (
                <Input
                  label="标题"
                  value={content.title || ''}
                  onChange={e => handleContentChange('title', e.target.value)}
                  placeholder="推送通知标题，支持 {变量名} 语法"
                  disabled={isPending}
                />
              )}

              {/* Body */}
              <Textarea
                label="正文"
                value={content.body || ''}
                onChange={e => handleContentChange('body', e.target.value)}
                placeholder="通知正文，支持 {变量名} 语法"
                rows={3}
                disabled={isPending}
                error={errors.body}
              />

              {/* Icon - for realtime */}
              {channels.includes('realtime') && (
                <Input
                  label="图标 (Ionicon 名称)"
                  value={content.icon || ''}
                  onChange={e => handleContentChange('icon', e.target.value)}
                  placeholder="例如: sparkles, heart, information-circle"
                  disabled={isPending}
                />
              )}

              {/* Action */}
              <ActionPicker
                value={content.action || ''}
                onChange={action => handleContentChange('action', action)}
                disabled={isPending}
              />

              {/* Action Label */}
              {content.action && (
                <Input
                  label="操作按钮文字"
                  value={content.action_label || ''}
                  onChange={e => handleContentChange('action_label', e.target.value)}
                  placeholder="查看"
                  disabled={isPending}
                />
              )}
            </div>

            {/* Variables Definition */}
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2 flex items-center justify-between">
                <span className="flex items-center gap-2">
                  <Variable className="w-4 h-4" />
                  模板变量定义
                </span>
                <button
                  type="button"
                  onClick={() => addVariable()}
                  disabled={isPending}
                  className="text-xs text-indigo-600 hover:text-indigo-700 font-normal flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" />
                  添加变量
                </button>
              </h3>

              {/* Undefined variables warning */}
              {undefinedVariables.length > 0 && (
                <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg">
                  <p className="text-sm text-amber-700 mb-2">
                    模板内容中使用了以下未定义的变量:
                  </p>
                  <div className="flex flex-wrap gap-2">
                    {undefinedVariables.map(varName => (
                      <button
                        key={varName}
                        type="button"
                        onClick={() => addVariable(varName)}
                        disabled={isPending}
                        className="inline-flex items-center gap-1 px-2 py-1 bg-amber-100 text-amber-800 rounded text-xs hover:bg-amber-200 transition-colors"
                      >
                        <code>{`{${varName}}`}</code>
                        <Plus className="w-3 h-3" />
                      </button>
                    ))}
                  </div>
                </div>
              )}

              {/* Variable list */}
              {variables.length > 0 ? (
                <div className="space-y-3">
                  {variables.map((v, index) => (
                    <div
                      key={index}
                      className="p-3 bg-gray-50 border border-gray-200 rounded-lg space-y-3"
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 grid grid-cols-2 gap-3">
                          <Input
                            label="变量名"
                            value={v.name}
                            onChange={e => updateVariable(index, 'name', e.target.value)}
                            placeholder="variable_name"
                            disabled={isPending}
                            className="text-sm"
                          />
                          <Input
                            label="显示名称"
                            value={v.label}
                            onChange={e => updateVariable(index, 'label', e.target.value)}
                            placeholder="变量显示名称"
                            disabled={isPending}
                            className="text-sm"
                          />
                        </div>
                        <button
                          type="button"
                          onClick={() => removeVariable(index)}
                          disabled={isPending}
                          className="p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded transition-colors mt-6"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>

                      <div className="grid grid-cols-3 gap-3">
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            类型
                          </label>
                          <select
                            value={v.type}
                            onChange={e => updateVariable(index, 'type', e.target.value as VariableType)}
                            disabled={isPending}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="string">字符串</option>
                            <option value="number">数字</option>
                            <option value="boolean">布尔值</option>
                          </select>
                        </div>
                        <div>
                          <label className="block text-xs font-medium text-gray-600 mb-1">
                            是否必填
                          </label>
                          <select
                            value={v.required ? 'true' : 'false'}
                            onChange={e => updateVariable(index, 'required', e.target.value === 'true')}
                            disabled={isPending}
                            className="w-full px-2 py-1.5 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                          >
                            <option value="true">必填</option>
                            <option value="false">可选</option>
                          </select>
                        </div>
                        <Input
                          label="默认值"
                          value={v.default || ''}
                          onChange={e => updateVariable(index, 'default', e.target.value)}
                          placeholder="可选"
                          disabled={isPending}
                          className="text-sm"
                        />
                      </div>

                      <Input
                        label="描述"
                        value={v.description || ''}
                        onChange={e => updateVariable(index, 'description', e.target.value)}
                        placeholder="说明此变量的用途"
                        disabled={isPending}
                        className="text-sm"
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-sm text-gray-500 p-3 bg-gray-50 rounded-lg text-center">
                  暂无变量定义，在模板内容中使用 <code className="bg-gray-200 px-1 rounded">{'{变量名}'}</code> 后可快速添加
                </p>
              )}
            </div>
          </div>

          {/* Right Column: Preview */}
          <div className="space-y-4 xl:sticky xl:top-0">
            <h3 className="text-sm font-medium text-gray-700 border-b border-gray-200 pb-2">
              效果预览
            </h3>

            <div className="bg-gray-50 rounded-xl p-4">
              <NotificationPreview
                channels={channels}
                content={content}
              />
            </div>

            {/* Defined variables summary */}
            <div className="p-4 bg-indigo-50 border border-indigo-100 rounded-xl">
              <h4 className="text-sm font-medium text-indigo-900 mb-2 flex items-center gap-2">
                <Variable className="w-4 h-4" />
                已定义的变量
              </h4>
              {variables.length > 0 ? (
                <div className="space-y-2">
                  {variables.map((v, i) => (
                    <div key={i} className="flex items-center gap-2 text-xs">
                      <code className="bg-indigo-100 px-1.5 py-0.5 rounded text-indigo-700 font-mono">
                        {`{${v.name}}`}
                      </code>
                      <span className="text-indigo-600">{v.label}</span>
                      {v.required && <span className="text-red-500">*</span>}
                      <span className="text-indigo-400 text-[10px]">({v.type})</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-indigo-600">
                  在左侧内容中使用 <code className="bg-indigo-100 px-1 rounded">{'{变量名}'}</code> 语法，
                  然后在"模板变量定义"中添加变量定义
                </p>
              )}
            </div>
          </div>
        </div>

        {/* Submit error */}
        {errors.submit && (
          <div className="flex items-center gap-2 p-3 mt-6 bg-red-50 border border-red-200 rounded-lg">
            <AlertCircle className="w-5 h-5 text-red-500 shrink-0" />
            <p className="text-sm text-red-700">{errors.submit}</p>
          </div>
        )}
      </CardContent>

      <CardFooter className="px-6 pb-6 pt-0">
        <div className="flex justify-end gap-3">
          <Button variant="secondary" onClick={onClose} disabled={isPending}>
            取消
          </Button>
          <Button onClick={handleSubmit} loading={isPending}>
            <Save className="w-4 h-4 mr-1" />
            {isEditing ? '保存更改' : '创建模板'}
          </Button>
        </div>
      </CardFooter>
    </Card>
  );
}
